import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQ_CATEGORIES = [
  {
    id: 'service',
    label: 'About the Service',
    items: [
      {
        q: 'How does commercial lease review differ from residential?',
        a: 'Commercial leases are governed by entirely different legislation, primarily the Landlord & Tenant Act 1954, the Lease Code 2007, and RICS best practice. Our commercial review is built on a specialist AI model trained exclusively on commercial lease law, covering rent reviews, break clauses, dilapidations, service charges, and alienation provisions that do not exist in residential agreements.',
      },
      {
        q: 'Does it cover international or foreign-governed leases?',
        a: 'Currently our service covers leases governed by English and Welsh law only. This includes all commercial property leases subject to the Landlord & Tenant Act 1954 and related UK commercial legislation. Scottish commercial leases are not yet supported.',
      },
    ],
  },
  {
    id: 'results',
    label: 'Understanding Results',
    items: [
      {
        q: 'What is the risk score and how is it calculated?',
        a: 'The risk score is a percentage reflecting overall lease compliance across all analysed areas including rent review mechanisms, break clause enforceability, dilapidations exposure, service charge provisions, and LTA 1954 compliance. Each clause is individually assessed and weighted by financial and legal impact.',
      },
      {
        q: 'What makes a break clause enforceable under LTA 1954?',
        a: 'For a break clause to be enforceable, conditions precedent must be achievable and clearly defined. Courts have held that conditions such as "all covenants performed" can render a break clause effectively unexercisable. Our review checks that break conditions are limited to material and subsisting breaches only, with clearly specified notice requirements.',
      },
    ],
  },
  {
    id: 'legal',
    label: 'Legal & Compliance',
    items: [
      {
        q: 'Can I use the review report in lease negotiations?',
        a: 'Yes. The report is designed to be used as a negotiation tool. Each finding includes a severity rating, plain-English explanation, and suggested counter-language. Many commercial agents and property managers use the report directly in negotiations with landlords or tenants.',
      },
      {
        q: 'What if the lease has already been signed?',
        a: 'A review is still valuable for signed leases. It identifies ongoing compliance risks, highlights clauses that may be unenforceable, and provides guidance on managing obligations throughout the lease term. For upcoming rent reviews or break dates, early identification of problematic clauses is critical.',
      },
      {
        q: 'Is the AI review accepted by courts or legal professionals?',
        a: 'Our review provides a thorough analysis based on current commercial lease legislation and case law. While it is not a substitute for formal legal advice, it identifies the same issues a specialist commercial property solicitor would. Many professionals use it as a first-pass analysis before instructing solicitors on specific points.',
      },
    ],
  },
  {
    id: 'specific',
    label: 'Specific Issues',
    items: [
      {
        q: 'How do you assess dilapidations exposure?',
        a: 'We analyse the repair and reinstatement covenants against the property description, any schedule of condition, and the yielding-up provisions. The assessment identifies whether the tenant\'s obligations go beyond the condition at lease commencement, whether there are ambiguous standards, and estimates the financial exposure range based on typical commercial dilapidations claims.',
      },
    ],
  },
];

export function CommercialFaqSection() {
  const [activeCategory, setActiveCategory] = useState('service');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const currentFaqs = FAQ_CATEGORIES.find((c) => c.id === activeCategory)?.items || [];

  return (
    <section className="py-20 sm:py-28 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-500">
            Everything you need to know about commercial lease reviews
          </p>
        </div>

        <div className="animate-on-scroll flex items-center justify-center gap-2 mb-8 flex-wrap">
          {FAQ_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setOpenIndex(0); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                activeCategory === cat.id
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="animate-on-scroll bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-elevated divide-y divide-gray-100">
          {currentFaqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={faq.q}>
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-start justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-3 pr-4">
                    <HelpCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-800">{faq.q}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 pl-[3.25rem] animate-slide-down">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
